import type { SortingState } from '@tanstack/react-table';
import { Table, type TableRowProps } from 'common/Table';
import { useCaseStudyParamsSuffix } from 'hooks/studyParams';
import { useFocusMode } from 'hooks/useFocusMode';
import { useReportTemplateValue } from 'hooks/useReportTemplate';
import { telemetryContextFor } from 'modules/Apollo/types';
import { FF } from 'modules/feature-flags/featureFlags.generated';
import { useFeatureFlagEnabled } from 'modules/feature-flags/useFeatureFlagEnabled';
import { createContext, memo, useCallback, useContext, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Colors } from 'styles';
import { rootTheme } from 'theme/rootTheme';
import { useHasClinicLevelTemplatePermission } from '../hooks/useHasClinicLevelTemplatePermission';
import { useRightDrawerTableColumns } from './useRightDrawerTableColumns';
import { RightDrawerAction } from './types';
import type { RightDrawerTableItemType, RightDrawerTabType } from './types';
import { orderStringToSortingState, sortingStateToOrderString } from './sorting';
import { RightDrawerTableRow } from './RightDrawerTableRow';
import { RightDrawerVirtualizedBody } from './RightDrawerVirtualizedBody';

type RightDrawerTableProps = {
  items: Array<RightDrawerTableItemType>;
  activeTab: RightDrawerTabType;
  basePath: string;
  starredId: Nullable<string>;
  isLoading: boolean;
  hasMoreData: boolean;
  onLoadMore: () => void;
  onRowClick: (arg1: RightDrawerTableItemType) => Promise<void>;
  onRowDoubleClick: (arg1: RightDrawerTableItemType) => Promise<void>;
  sorting?: string;
  onSortingChange?: (arg1: string) => void;
  isInsertColumnEnabled?: boolean;
};

type RightDrawerTableRowContextValue = Pick<
  React.ComponentProps<typeof RightDrawerTableRow>,
  'activeTab' | 'getRowAction' | 'onRowClick' | 'onRowDoubleClick' | 'selectedTemplateId'
>;

const RightDrawerTableRowContext = createContext<RightDrawerTableRowContextValue | null>(null);

// Kept at module scope so React always sees the same component identity for a given row.
// Defining the row inside RightDrawerTable (a closure over the callbacks) would produce a new
// component identity whenever a callback changed, which remounts every row
function RightDrawerTableRowAdapter(
  props: TableRowProps<RightDrawerTableItemType>
): React.ReactElement {
  const context = useContext(RightDrawerTableRowContext);

  if (context == null) {
    throw new Error('RightDrawerTableRowAdapter must be rendered inside RightDrawerTable');
  }

  return <RightDrawerTableRow {...props} {...context} />;
}

RightDrawerTableRowAdapter.displayName = 'RightDrawerTableRowAdapter';

const getRightDrawerRowId = (item: RightDrawerTableItemType): string => item.id;

export const RightDrawerTable = memo(
  ({
    items,
    activeTab,
    basePath,
    starredId,
    onRowClick,
    isLoading,
    onLoadMore,
    hasMoreData,
    onRowDoubleClick,
    sorting,
    onSortingChange,
    isInsertColumnEnabled = false,
  }: RightDrawerTableProps): React.ReactElement | null => {
    const { worklistId } = useParams<{ worklistId?: string }>();
    const priorsSuffix = useCaseStudyParamsSuffix(worklistId ?? null);
    const navigate = useNavigate();
    const { isFocusModeOn } = useFocusMode({ context: APOLLO_QUERY_CONTEXT });
    const hasClinicLevelTemplatePermission = useHasClinicLevelTemplatePermission({
      context: APOLLO_QUERY_CONTEXT,
    });
    const [isVirtualizationEnabled] = useFeatureFlagEnabled(
      FF.REPORTER_RIGHT_DRAWER_TABLE_VIRTUALIZATION
    );
    const template = useReportTemplateValue();

    const sortingState = useMemo(() => orderStringToSortingState(sorting), [sorting]);
    const tableState = useMemo(() => ({ sorting: sortingState }), [sortingState]);

    const handleSortingChange = useCallback(
      (nextSortingState: SortingState) => {
        const sortingString = sortingStateToOrderString(nextSortingState);
        if (sortingString && onSortingChange) onSortingChange(sortingString);
      },
      [onSortingChange]
    );

    const getRowAction = useCallback((item: RightDrawerTableItemType) => {
      return {
        id: item.id,
        name: item.name,
        action:
          item.owner != null && item.id != null ? RightDrawerAction.Edit : RightDrawerAction.Clone,
      } as const;
    }, []);

    const columns = useRightDrawerTableColumns({
      activeTab,
      basePath,
      priorsSuffix,
      isFocusModeOn,
      hasClinicLevelTemplatePermission,
      navigate,
      starredId,
      onSetSorting: handleSortingChange,
      isInsertColumnEnabled,
      onRowDoubleClick,
      getRowAction,
    });

    const rowContextValue = useMemo<RightDrawerTableRowContextValue>(
      () => ({
        activeTab,
        getRowAction,
        onRowClick,
        onRowDoubleClick,
        selectedTemplateId: template?.id,
      }),
      [activeTab, getRowAction, onRowClick, onRowDoubleClick, template?.id]
    );

    return (
      <RightDrawerTableRowContext.Provider value={rowContextValue}>
        <Table
          data-testid={`${activeTab}-table`}
          css={`
            background-color: ${Colors.gray1};
            border-radius: ${rootTheme.radius.sm}px;

            .table-header {
              background-color: ${Colors.gray2};
              padding: 0px 16px 0px 0px;
              border-radius: ${rootTheme.radius.sm}px ${rootTheme.radius.sm}px 0px 0px;
            }
          `}
          columns={columns}
          data={items}
          Row={RightDrawerTableRowAdapter}
          Body={isVirtualizationEnabled ? RightDrawerVirtualizedBody : undefined}
          getRowId={getRightDrawerRowId}
          hasNextPage={hasMoreData}
          loading={isLoading}
          state={tableState}
          manualSorting
          onLoadMore={onLoadMore}
        />
      </RightDrawerTableRowContext.Provider>
    );
  }
);

RightDrawerTable.displayName = 'RightDrawerTable';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(RightDrawerTable);
